import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Input from '../../../../../components/Input';
import Dropdown from '../../../../../components/Dropdown';
import Switch from '../../../../../components/Switch';
import TableBox from '../../../../../components/TableBox';
import { VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { TableContextProvider, Validation } from '../../../../../interfaces/common';
import { AddEdit as AddEditInterface } from '../../../../../interfaces/property.interface';

interface MaintenanceDetailsProps {
  formData: AddEditInterface;
  validation: Validation;
  values: TableContextProvider
  view?: boolean;
  managers: any[];
  allManagers: any[];
  bookingTypes: any[];
  allBookingTypes: any[];
  allWhoCollectsMoneyOptions: any[];
  cleaningManagedByOptions: any[]
  dropdownSearchData: any;
  error?: boolean;
  errorMessage?: string;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const MaintenanceDetails: React.FC<MaintenanceDetailsProps> = ({
  formData,
  validation,
  values,
  view,
  managers,
  allManagers,
  bookingTypes,
  allBookingTypes,
  allWhoCollectsMoneyOptions,
  cleaningManagedByOptions,
  dropdownSearchData,
  error,
  errorMessage,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange
}) => {
  const { t } = useTranslation();

  return (
    <div className="row g-6">
      <h6 className="mb-0">
        3. {t('property.addEdit.pageTexts.form.sections.maintenanceDetail')}
      </h6>

      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.bookingType ? [formData.bookingType] : []}
            options={(bookingTypes || []).map((bookingType: any) => ({
              label: bookingType.name,
              value: bookingType.id
            }))}
            allOptions={(allBookingTypes || []).map((bookingType: any) => ({
              label: bookingType.name,
              value: bookingType.id
            }))}
            id={VALIDATION_FIELD_MAPPING.bookingType}
            name={VALIDATION_FIELD_MAPPING.bookingType}
            disabled={view}
            searchText={dropdownSearchData.bookingType}
            onSearch={onDropdownSearchChange}
            placeholder={t('property.addEdit.pageTexts.form.bookingType.placeholder')}
            label={t('property.addEdit.pageTexts.form.bookingType.label')}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.bookingType)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.bookingType)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            required
            id={VALIDATION_FIELD_MAPPING.commissionPercentage}
            name={VALIDATION_FIELD_MAPPING.commissionPercentage}
            disabled={view}
            label={t('property.addEdit.pageTexts.form.commission.label')}
            placeholder={t('property.addEdit.pageTexts.form.commission.placeholder')}
            value={formData.commissionPercentage}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.commissionPercentage)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.commissionPercentage)?.message ?? ''}
            onKeyDown={onKeyDown}
            onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.commissionPercentage)}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.commissionPercentage)}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.managedBy ? [formData.managedBy] : []}
            options={(managers || []).map((manager: any) => ({
              label: manager.name,
              value: manager.id
            }))}
            allOptions={(allManagers || []).map((manager: any) => ({
              label: manager.name,
              value: manager.id
            }))}
            id={VALIDATION_FIELD_MAPPING.managedBy}
            name={VALIDATION_FIELD_MAPPING.managedBy}
            disabled={view}
            searchText={dropdownSearchData.managedBy}
            placeholder={t('property.addEdit.pageTexts.form.managedBy.placeholder')}
            label={t('property.addEdit.pageTexts.form.managedBy.label')}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.managedBy)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.managedBy)?.message ?? ''}
            onChange={onInputChange}
            onSearch={onDropdownSearchChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.cleaningManagedBy ? [formData.cleaningManagedBy] : []}
            options={(cleaningManagedByOptions || []).map((item: any) => ({
              label: item.name,
              value: item.id
            }))}
            allOptions={(allWhoCollectsMoneyOptions || []).map((item: any) => ({
              label: item.name,
              value: item.id
            }))}
            id={VALIDATION_FIELD_MAPPING.cleaningManagedBy}
            name={VALIDATION_FIELD_MAPPING.cleaningManagedBy}
            disabled={view}
            required
            searchText={dropdownSearchData.cleaningManagedBy}
            placeholder={t('property.addEdit.pageTexts.form.cleaningManagedBy.placeholder')}
            label={t('property.addEdit.pageTexts.form.cleaningManagedBy.label')}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.cleaningManagedBy)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.cleaningManagedBy)?.message ?? ''}
            onChange={onInputChange}
            onSearch={onDropdownSearchChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>

      <div className="col-md-3 d-flex align-items-center">
        <Switch
          name={VALIDATION_FIELD_MAPPING.configureMatTax}
          label={t('property.addEdit.pageTexts.form.configureMatTax.label')}
          checked={formData.configureMatTax}
          disabled={view}
          onChange={onInputChange}
        />
      </div>

      {formData.configureMatTax && (
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.matTaxPercentage}
              name={VALIDATION_FIELD_MAPPING.matTaxPercentage}
              disabled={view}
              label={t('property.addEdit.pageTexts.form.matTaxPercentage.label')}
              placeholder={t('property.addEdit.pageTexts.form.matTaxPercentage.placeholder')}
              value={formData.matTaxPercentage}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.matTaxPercentage)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.matTaxPercentage)?.message ?? ''}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.matTaxPercentage)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.matTaxPercentage)}
            />
          </div>
        </div>
      )}

      <div className="col-md-12">
        <TableBox tableValues={values} />
        {
          error && (
            <div className={clsx("fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback", error && 'd-block')}>
              <div data-validator="notEmpty">
                {errorMessage}
              </div>
            </div>
          )
        }
      </div>
    </div>
  )
}

export default MaintenanceDetails;